import { useEffect, useRef, useState } from "react";
import { Check, SlidersHorizontal } from "lucide-react";
import type { ColumnDef } from "@/hooks/useColumnVisibility";

const BORDER = "hsl(210,30%,85%)";
const ICON_COLOR = "hsl(215,16%,47%)";
const PRIMARY = "hsl(210,100%,45%)";

export interface ColumnSelectorButtonProps {
  columns: ColumnDef[];
  visibility: Record<string, boolean>;
  onToggle: (key: string) => void;
  onReset: () => void;
  minReached?: boolean;
  className?: string;
}

export function ColumnSelectorButton({
  columns,
  visibility,
  onToggle,
  onReset,
  minReached = false,
  className,
}: ColumnSelectorButtonProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={"relative inline-flex" + (className ? ` ${className}` : "")} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Column Settings"
        aria-label="Column Settings"
        aria-expanded={open}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white transition-colors hover:bg-[hsl(210,30%,97%)]"
        style={{ borderColor: BORDER, color: ICON_COLOR }}
      >
        <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} />
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 overflow-hidden rounded-lg border bg-white"
          style={{
            top: "calc(100% + 4px)",
            borderColor: BORDER,
            width: 220,
            maxHeight: 360,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{ padding: "10px 14px 8px" }}
          >
            <span
              className="text-[12px] font-semibold uppercase"
              style={{ color: ICON_COLOR, letterSpacing: "0.04em" }}
            >
              Columns
            </span>
            <button
              type="button"
              onClick={onReset}
              className="bg-transparent text-[10px] font-medium uppercase tracking-wide hover:underline"
              style={{ color: PRIMARY }}
            >
              Reset
            </button>
          </div>
          <div
            style={{
              maxHeight: 312,
              overflowY: "auto",
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            {columns.map((col) => {
              const checked = Boolean(visibility[col.key]);
              const disableUncheck = checked && minReached;
              return (
                <button
                  key={col.key}
                  type="button"
                  onClick={() => {
                    if (disableUncheck) return;
                    onToggle(col.key);
                  }}
                  title={
                    disableUncheck
                      ? "At least 2 columns must be visible"
                      : undefined
                  }
                  className="flex h-8 w-full items-center gap-2 bg-transparent text-left transition-colors hover:bg-[hsl(210,30%,97%)] disabled:cursor-not-allowed"
                  style={{ padding: "0 14px" }}
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border"
                    style={{
                      background: checked ? PRIMARY : "white",
                      borderColor: checked ? PRIMARY : BORDER,
                    }}
                  >
                    {checked && (
                      <Check
                        className="h-3 w-3"
                        strokeWidth={3}
                        style={{ color: "white" }}
                      />
                    )}
                  </span>
                  <span
                    className="text-[13px]"
                    style={{
                      color: disableUncheck
                        ? "hsl(215,12%,60%)"
                        : "hsl(215,25%,20%)",
                    }}
                  >
                    {col.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
